import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

const MONO = "'JetBrains Mono', 'Fira Code', Consolas, Monaco, monospace"

interface Case {
  title: string
  metric?: string
  desc: string
  relato: string
}

interface Category {
  id: string
  label: string
  dot: string
  tags: string[]
  cases: Case[]
}

const CATEGORIES: Category[] = [
  {
    id: 'automacao',
    label: 'Automação',
    dot: 'var(--green)',
    tags: ['n8n', 'WP-AUDIT', 'WAF', 'Fallback', 'Redis', 'Webhooks'],
    cases: [
      {
        title: 'Sistema de fallback de formulários',
        metric: '4.200 leads · R$200k+',
        desc: 'GravityForms caiu silenciosamente em produção. Construí fallback em PHP + n8n em 48h. Recuperou 4.200 leads e R$200k+ em receita que teriam sumido.',
        relato: 'Numa sexta à noite vi os dados de leads parando de entrar. O GravityForms estava caindo silenciosamente há horas: sem log, sem alerta, sem fallback. Primeiro instinto foi calcular a perda. Construí o sistema em PHP puro + n8n em 48h. O mais difícil não foi o código: foi a reconciliação retroativa dos leads perdidos sem duplicar nenhum no CRM. Aprendi que monitoramento de formulários é tão crítico quanto monitoramento de servidor.',
      },
      {
        title: '47 workflows n8n em produção',
        metric: '1 main + 2 workers + 2 webhooks + Redis',
        desc: 'Pipeline em queue mode: enriquecimento de leads, geração de copy, sync ClickUp, relatórios diários de performance. Zero downtime há 6 meses.',
        relato: 'Começou com 3 workflows manuais. Hoje são 47 em queue mode. A transição de single-node para queue foi o momento mais delicado: qualquer downtime para o fluxo de leads do time comercial. Fiz a migração num domingo de manhã com plano de rollback testado. A arquitetura de 1 main + 2 workers + Redis foi o que me deu escala sem custo extra de infra. A chave foi tratar cada worker como descartável desde o design.',
      },
      {
        title: 'WP-AUDIT toolkit',
        metric: '9 servidores · 16 camadas',
        desc: 'Scanner bash de 16 camadas: detecta webshells, backdoors MySQL, PHP em uploads, divergências de checksum, mu-plugins não autorizados. Roda em cron diário.',
        relato: 'Depois do incidente de RCE, precisava de visibilidade sistemática nos 9 servidores. Auditar manualmente era inviável. Construí o toolkit em bash com 16 camadas, cada uma testando um vetor diferente. A mais surpreendente foi a de checksum de arquivos WordPress core: revelou 3 webshells extras que os scanners comerciais não pegaram. O scanner agora roda diariamente via cron e manda alerta por webhook.',
      },
      {
        title: 'SDL: Sistema de Roteamento de Leads',
        metric: '4k+ leads qualificados',
        desc: 'Pipeline n8n que coleta, higieniza, enriquece (Instagram, LinkedIn, cargo, empresa) e distribui leads com contexto completo pro time comercial.',
        relato: 'O time comercial recebia leads frios: nome, email, telefone. Sem cargo, sem empresa, sem intenção. O SDL mudou isso. O pipeline coleta, higieniza, enriquece com dados de perfil via scraping e só distribui quando tem contexto suficiente para o vendedor abrir a conversa já sabendo com quem está falando. A taxa de resposta do time subiu porque os leads chegam com contexto real, não só dados de formulário.',
      },
    ],
  },
  {
    id: 'ia',
    label: 'IA',
    dot: 'var(--purple)',
    tags: ['AIOS', 'Claude', 'Agents', 'LLMs', 'GEO', 'RAG'],
    cases: [
      {
        title: 'AIOS: AI Operating System',
        metric: '20+ agentes · MCPs por papel · em produção',
        desc: 'Framework de agentes Claude Code com least-privilege: agente de tráfego acessa Meta Ads API, agente de dados acessa BigQuery, criativo não vê credencial nenhuma. KB própria + memória persistente por agente.',
        relato: 'O problema com LLMs genéricos é re-explicar contexto toda sessão. O AIOS resolveu isso: cada agente tem KB própria, memória persistente e protocolo de execução específico. O agente de tráfego sabe o histórico de campanhas. O de cyber sabe os incidentes anteriores. O CFO sabe o fluxo de caixa. É como ter um time especializado que nunca esquece, com permissões distintas por função. O design de least-privilege foi o que tornou possível usar em ambiente corporativo.',
      },
      {
        title: 'GEO em 280 landing pages',
        metric: '280 LPs · GPTBot + ClaudeBot + PerplexityBot',
        desc: 'Generative Engine Optimization: llms.txt, robots.txt com Content-Signal, JSON-LD e H2 como perguntas naturais. Primeiro no setor de financiamento B2B brasileiro.',
        relato: 'O ChatGPT não citava nada do nosso setor quando alguém perguntava sobre financiamento de equipamentos. Pesquisei o que os LLMs indexam: robots.txt com Content-Signal alinhado ao EU AI Act, llms.txt com contexto de negócio em markdown, H2 como perguntas naturais que o modelo indexa como resposta. Aplicamos nas 280 LPs e em 2 semanas o ChatGPT passou a citar nosso conteúdo. Ninguém no setor B2B brasileiro tinha feito isso ainda. O diferencial foi entender que LLMs leem de forma diferente de crawlers SEO tradicionais.',
      },
      {
        title: 'Race condition em 11.007 leads',
        metric: '11.007 leads corrompidos · resolvido em 1 sessão',
        desc: 'Bug silencioso em pipeline de sync corrompeu atribuição de 11k leads. Identifiquei com SQL de auditoria, resolvi com locks de transação e reconciliação retroativa. Zero perda.',
        relato: 'Bug silencioso: o pipeline de sync sobrescrevia a atribuição de leads quando dois eventos chegavam em milissegundos. 11.007 leads com atribuição corrompida no CRM. Identifiquei cruzando timestamps com SQL de auditoria. Resolvi com locks de transação e fiz reconciliação retroativa linha por linha. Zero perda de dado. A parte mais difícil foi provar para o time que o dado original ainda estava recuperável, porque a perda parecia irreversível no sistema de CRM.',
      },
      {
        title: 'Pipeline de enriquecimento por IA',
        metric: 'Apify + Claude + Supabase',
        desc: 'Scraping de perfil (Instagram handle → dados públicos), scoring por cargo/empresa com Claude, armazenamento em Supabase como SSoT. Taxa de match: 60-70% por lead.',
        relato: 'Instagram handle para dados completos de negócio parece simples mas tem 30-40% de falso positivo no match. Construí um sistema de scoring com Claude que analisa o perfil e dá uma nota de confiança antes de marcar o lead como enriquecido. Taxa de match real ficou em 60-70%. O restante vai para revisão manual. É a automação mais honesta que já construí: foi desenhada pra saber quando não sabe, em vez de forçar um resultado errado.',
      },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing',
    dot: 'var(--blue)',
    tags: ['GTM', 'Meta', 'Google', 'Revenue', 'BigQuery', 'Analytics'],
    cases: [
      {
        title: 'Empresa B2B · R$350M/ano impactados',
        metric: '60k leads · CWV 47→90 · GEO 280 LPs',
        desc: 'Growth Engineer na infra de performance: 60k leads gerados, PageSpeed mobile 47→89 em 9 sites (+41pts, 2 semanas), GEO em 280 LPs, pipeline Meta + Google Ads (9 contas) → BigQuery.',
        relato: 'Trabalhar em escala onde um erro de tracking custa R$50k/dia em decisões erradas. Cada mudança precisa de validação antes de ir para produção. O que aprendi aqui: instrumentação não é detalhe, é fundação. Sem dados confiáveis, todo o resto é suposição. O pipeline Meta + Google Ads → BigQuery foi o que me deu clareza real sobre o que funcionava. Antes dele, o time tomava decisões com data studio desatualizado.',
      },
      {
        title: 'Agência de Performance: R$0 → R$40k MRR',
        metric: 'R$9M+ ads · 15 clientes · 30 LPs',
        desc: 'Agência de performance construída do zero em 18 meses, 2 pessoas, zero investidor. 40+ LPs entregues em 3 meses para clientes gerando 50k+ visitas orgânicas. Meta · Google · TikTok.',
        relato: 'Comecei com R$0, zero cliente e sem saber exatamente o que seria. O primeiro cliente veio de indicação. O segundo do LinkedIn. Aprendi que agência de performance é, na prática, uma empresa de sistemas: quem tem melhor infra escala mais rápido. Por isso investi em AIOS, SDL e automações antes de escalar equipe. O crescimento de R$0 a R$40k MRR em 18 meses foi consequência da infra, não de volume de vendas.',
      },
      {
        title: 'Pipeline B.I. em produção',
        metric: 'Meta Ads + Google Ads → BigQuery',
        desc: 'ETL automático: coleta dados das APIs de ads via n8n, trata e carrega no BigQuery, alimenta dashboards com métricas de CAC, CPL, ROAS por segmento e por LP.',
        relato: 'A Meta Ads API e Google Ads API retornam dados em formatos completamente diferentes. Normalizar isso de forma que faça sentido para o time de mídia, sem conhecimento técnico, foi o maior desafio. Levei 3 iterações até o dashboard que o time usa de fato todo dia. A versão 1 era "tecnicamente correta". A versão 3 era "útil". A diferença estava em entender que o time olha CPL por LP, não por campanha.',
      },
      {
        title: 'Laxtro: SaaS de conteúdo white label',
        metric: 'Em construção · 2026',
        desc: 'Pipeline completo para agências de conteúdo: DNA editorial da marca → pautas → roteiros → assets visuais (HTML→PNG) → captions por plataforma. Multi-tenant, geração por IA, cron mensal automático.',
        relato: 'Laxtro nasceu de um problema real que eu via nas agências: cada cliente é gerenciado em planilha, o conteúdo é criado no improviso e não existe processo. O Laxtro resolve isso com pipeline: primeiro você cadastra o DNA da marca (voz, posicionamento, pilares), depois o sistema gera pautas, roteiros e assets automaticamente. A decisão mais difícil foi arquitetura multi-tenant desde o dia 0. Encarece o início mas é a única forma de escalar sem reescrever depois.',
      },
    ],
  },
  {
    id: 'cyber',
    label: 'Cyber',
    dot: 'var(--red)',
    tags: ['WP Hardening', 'Incident Response', 'Infra Sec', 'Auditoria'],
    cases: [
      {
        title: 'RCE via FTP comprometido',
        metric: 'Backdoor persistente · 9 sites WordPress',
        desc: 'Credenciais FTP comprometidas resultaram em backdoor persistente em 9 sites. Identifiquei vetor, removi artefatos maliciosos, hardeni infra e escrevi relatório de incidente completo.',
        relato: 'Credenciais FTP vazaram e o atacante instalou backdoor em 9 sites de uma vez. O mais assustador: o backdoor tinha mecanismo de persistência. Se removido, se reinstalava via outro vetor. Tive que bloquear FTP, isolar os sites, fazer análise forense e escrever o relatório de incidente tudo ao mesmo tempo. Aprendi mais sobre segurança em 48h do que em meses de estudo teórico. Cyber na prática é diferente de cyber no livro.',
      },
      {
        title: '9 SQL dumps expostos (480MB cada)',
        metric: 'Dados de clientes em /public_html',
        desc: 'Dumps de banco de dados estavam acessíveis publicamente. Baixei, analisei o conteúdo, deletei do servidor e documentei o incidente antes que qualquer crawler indexasse.',
        relato: 'Estava fazendo uma auditoria de rotina quando encontrei os dumps. 9 arquivos de 480MB cada, acessíveis publicamente no diretório web. Dados de clientes, senhas com encoding fraco em alguns casos. Baixei tudo, analisei, deletei do servidor e documentei antes que qualquer crawler indexasse. A velocidade importou: um bot do Google passando por ali naquele momento teria sido catastrófico para LGPD e para a reputação.',
      },
      {
        title: 'Hardening em 9 servidores WordPress',
        metric: 'SSH + UFW + fail2ban + WAF + mu-plugins',
        desc: 'Stack completo: SSH key-only, fail2ban, UFW, xmlrpc.php bloqueado, WAF customizado em mu-plugin, monitor com alertas. PageSpeed médio subiu 10,4pts no processo.',
        relato: 'O hardening precisava ser invisível para o usuário final e para o Elementor e GravityForms que já rodavam em produção. Cada mu-plugin foi testado individualmente antes de ir para todos os servidores. O PageSpeed subindo 10,4pts foi efeito colateral: o WAF em mu-plugin não carrega JavaScript desnecessário como plugins comuns fazem. Segurança e performance, nesse caso, andaram juntas.',
      },
      {
        title: 'Auditoria de 22 bancos + 37 instâncias WP',
        metric: '5 VPS · checklist de 16 itens',
        desc: 'Varredura completa: triggers MySQL, admins não autorizados, temas inativos com backdoors, plugins banidos, versões críticas. Zero triggers maliciosos. 3 webshells extras removidos.',
        relato: 'Depois do incidente de RCE, a diretoria pediu auditoria completa. Criei um checklist de 16 itens e rodei em todos os 5 VPS sistematicamente. O que mais me surpreendeu: 3 webshells extras que não estavam relacionados ao incidente principal. O ambiente já estava comprometido de uma forma que não sabíamos antes da auditoria. Isso mudou como penso em segurança: o incidente que você conhece raramente é o único.',
      },
    ],
  },
]

export default function ExpertiseAccordion() {
  const [openCats, setOpenCats] = useState<Set<string>>(new Set())
  const [activeRelato, setActiveRelato] = useState<string | null>(null)

  const toggleCat = (id: string) => {
    setOpenCats(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleRelato = (key: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setActiveRelato(prev => prev === key ? null : key)
  }

  return (
    <section style={{ maxWidth: 1100, margin: '0 auto', padding: '0 1.5rem 5rem' }}>
      <div style={{
        fontFamily: MONO,
        fontSize: '0.68rem',
        color: 'var(--muted)',
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        marginBottom: '1.25rem',
      }}>
        casos detalhados
      </div>

      <div style={{ borderTop: '1px solid var(--border)' }}>
        {CATEGORIES.map((cat) => {
          const isOpen = openCats.has(cat.id)
          return (
            <div key={cat.id} style={{ borderBottom: '1px solid var(--border)' }}>
              {/* Header row */}
              <button
                onClick={() => toggleCat(cat.id)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.85rem',
                  padding: '1.1rem 0',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'opacity 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >
                {/* Dot */}
                <span style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: 'var(--muted)',
                  flexShrink: 0,
                }} />

                {/* Label */}
                <span style={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontSize: '1.05rem',
                  fontWeight: 700,
                  color: 'var(--text)',
                  letterSpacing: '-0.01em',
                  minWidth: 110,
                  flexShrink: 0,
                }}>
                  {cat.label}
                </span>

                {/* Tags */}
                <span style={{
                  fontFamily: MONO,
                  fontSize: '0.78rem',
                  color: 'var(--muted)',
                  letterSpacing: '0.01em',
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {cat.tags.join(' · ')}
                </span>

                {/* Toggle icon */}
                <motion.span
                  animate={{ rotate: isOpen ? 45 : 0 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 22,
                    height: 22,
                    border: '1px solid var(--border-strong)',
                    borderRadius: 4,
                    color: 'var(--muted)',
                    fontSize: '1rem',
                    lineHeight: 1,
                    flexShrink: 0,
                    fontFamily: MONO,
                  }}
                >
                  +
                </motion.span>
              </button>

              {/* Expanded content */}
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    key="content"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    style={{ overflow: 'hidden' }}
                  >
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                      gap: '1px',
                      background: 'var(--border)',
                      margin: '0 0 1.5rem 0',
                      borderRadius: 8,
                      overflow: 'hidden',
                    }}>
                      {cat.cases.map((c, i) => {
                        const relatoKey = `${cat.id}-${i}`
                        const relatoOpen = activeRelato === relatoKey
                        return (
                          <div
                            key={i}
                            onClick={e => toggleRelato(relatoKey, e)}
                            style={{
                              background: relatoOpen ? 'var(--bg3)' : 'var(--bg2)',
                              padding: '1.25rem 1.4rem',
                              cursor: 'pointer',
                              transition: 'background 0.15s',
                            }}
                            onMouseEnter={e => { if (!relatoOpen) (e.currentTarget as HTMLDivElement).style.background = 'var(--bg3)' }}
                            onMouseLeave={e => { if (!relatoOpen) (e.currentTarget as HTMLDivElement).style.background = 'var(--bg2)' }}
                          >
                            {c.metric && (
                              <div style={{
                                fontFamily: MONO,
                                fontSize: '0.68rem',
                                color: 'var(--muted)',
                                letterSpacing: '0.08em',
                                marginBottom: '0.45rem',
                              }}>
                                {c.metric}
                              </div>
                            )}
                            <div style={{
                              fontSize: '0.95rem',
                              fontWeight: 700,
                              color: 'var(--text)',
                              letterSpacing: '-0.02em',
                              marginBottom: '0.5rem',
                              lineHeight: 1.3,
                            }}>
                              {c.title}
                            </div>
                            <div style={{
                              fontSize: '0.82rem',
                              color: 'var(--muted)',
                              lineHeight: 1.55,
                            }}>
                              {c.desc}
                            </div>

                            {/* Relato trigger */}
                            <div style={{
                              marginTop: '0.85rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.35rem',
                              fontFamily: MONO,
                              fontSize: '0.65rem',
                              color: relatoOpen ? 'var(--text)' : 'var(--muted)',
                              letterSpacing: '0.08em',
                              transition: 'color 0.15s',
                            }}>
                              <motion.span
                                animate={{ rotate: relatoOpen ? 180 : 0 }}
                                transition={{ duration: 0.2 }}
                                style={{ display: 'inline-block' }}
                              >
                                ▼
                              </motion.span>
                              {relatoOpen ? 'fechar relato' : 'relato pessoal'}
                            </div>

                            {/* Relato expanded */}
                            <AnimatePresence>
                              {relatoOpen && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.25, ease: 'easeOut' }}
                                  style={{ overflow: 'hidden' }}
                                >
                                  <div style={{
                                    marginTop: '1rem',
                                    paddingTop: '1rem',
                                    borderTop: '1px solid var(--border)',
                                    fontSize: '0.8rem',
                                    color: 'var(--muted)',
                                    lineHeight: 1.7,
                                    fontStyle: 'italic',
                                  }}>
                                    {c.relato}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        )
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )
        })}
      </div>
    </section>
  )
}
